"use client";

import Link from "@/app/[lang]/_components/Link";
import { LoadingSpinner } from "@/app/[lang]/_components/LoadingSpinner";
import { createT } from "@/app/[lang]/_utils/create-t";
import ClapIcon from "@/assets/components/Clap";
import ClappedIcon from "@/assets/components/Clapped";
import ReplyIcon from "@/assets/components/Reply";
import type { Lang } from "@/langs";
import type { CommentDetailsNested } from "@/server/api/routers/comment";
import { api } from "@/trpc/react";
import type { Post } from "@/validators/post";
import {
  type Editor,
  EditorContent,
  extensionsStoryComment,
  useEditor,
} from "@borderless/editor";
import { Button } from "@borderless/ui/components/button";
import { Card } from "@borderless/ui/components/card";
import { toast } from "@borderless/ui/components/sonner";
import { Spinner } from "@borderless/ui/components/spinner";
import { cn } from "@borderless/ui/lib/utils";
import { MessageCircleMore, Share } from "lucide-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import type { FC } from "react";
import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { ThreeGridLayout } from "../../../../../_components/ThreeGridLayout";
import { CommentAuthorSection } from "../CommentAuthorSection";
import CommentTree from "../CommentTree";
import { flattenComments } from "../flatten-comments";
import dictionary from "./t.json";

const t = createT(dictionary);

const useEditorStyled = () => {
  return useEditor(
    {
      immediatelyRender: false,
      extensions: extensionsStoryComment({
        placeholder: "What are your thoughts?",
        placeholderEmptyNodeClass:
          "first:before:h-0 first:before:text-subtle first:before:float-left first:before:content-[attr(data-placeholder)] first:before:pointer-events-none first:before:text-base first:before:leading-relaxed sm:first:before:text-base sm:first:before:leading-relaxed md:first:before:text-base md:first:before:leading-relaxed lg:first:before:text-base lg:first:before:leading-relaxed xl:first:before:text-base xl:first:before:leading-relaxed",
      }),
      editable: true,
      content: "",
      editorProps: {
        attributes: {
          class: "h-full min-h-32 w-full focus:outline-hidden",
        },
      },
    },
    [],
  );
};
export const commentEditorId = "post-comment-editor";
const PostMinimalAction: FC<{
  postTitle: string;
  postSlug: string;
  commentsCount: number;
  claps: {
    userId: string;
  }[];
  onCommentClick: () => void;
  className?: string;
}> = (props) => {
  const { data: currentUser } = api.user.findSelf.useQuery();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  return (
    <div className={cn("flex items-center gap-4", props.className)}>
      <ClapButtonForPost
        Icon={
          props.claps.some((clap) => clap.userId === currentUser?.id) ? (
            <ClappedIcon />
          ) : (
            <ClapIcon />
          )
        }
        postSlug={props.postSlug}
        userId={currentUser?.id}
        claps={props.claps}
      />
      <Button
        variant="secondary"
        onClick={(e) => {
          e.preventDefault();
          if (currentUser === null) {
            // user is not logged in
            const params = new URLSearchParams(searchParams);
            params.set("callbackUrl", pathname);
            router.push(`/login?${params.toString()}`);
          } else {
            props.onCommentClick();
          }
        }}
      >
        <MessageCircleMore />
        {props.commentsCount === 0 ? "" : props.commentsCount.toString()}
      </Button>
      <Button
        variant="secondary"
        onClick={(e) => {
          e.preventDefault();
          const shareData = {
            title: "Borderless | " + props.postTitle,
            url: `https://borderless.so/stories/${props.postSlug}?utm_source=borderless&utm_medium=share`,
          };
          if (navigator.canShare(shareData)) {
            void navigator.share(shareData).catch((e) => {
              // do nothing
            });
          } else {
            void navigator.clipboard.writeText(shareData.url);
            toast.success("🔗 Copied to clipboard");
          }
        }}
      >
        <Share /> Share
      </Button>
    </div>
  );
};
export const PostAction: FC<{
  post: Post;
  lang: Lang;
}> = ({ post, lang }) => {
  const { data: claps } = api.clap.findClapsOnPost.useQuery({
    postSlug: post.slug,
  });
  const { data: comments } = api.comment.findMany.useQuery({
    slug: post.slug,
  });

  const editor = useEditorStyled();
  const onCommentClick = () => {
    const el = document.getElementById(commentEditorId);
    if (el) {
      const rect = el.getBoundingClientRect();
      if (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom - rect.height <=
          (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <=
          (window.innerWidth || document.documentElement.clientWidth)
      ) {
        // In viewport, focus
        editor?.commands.focus();
      } else {
        // Not in viewport, scroll
        el.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      editor?.commands.focus();
    }
  };

  if (!claps) return null;
  if (!comments) return null;

  const commentsCount = flattenComments(comments).length;

  return (
    <>
      <ThreeGridLayout
        left={null}
        center={
          <PostMinimalAction
            postTitle={post.title}
            postSlug={post.slug}
            commentsCount={commentsCount}
            claps={claps}
            onCommentClick={onCommentClick}
            className="pointer-events-auto bottom-0 flex h-full w-full items-center justify-center bg-background py-2"
          />
        }
        right={null}
        className="pointer-events-none sticky bottom-0 gap-0 bg-transparent"
      />
      <ThreeGridLayout
        left={null}
        center={
          <>
            <div className="h-10" />
            <CommentEditor postSlug={post.slug} editor={editor} lang={lang} />
          </>
        }
        right={null}
        className="gap-0 lg:gap-8"
      />

      <div className="h-4" />
      <ThreeGridLayout
        left={null}
        center={
          <CommentTree postSlug={post.slug} comments={comments} lang={lang} />
        }
        right={null}
        className="gap-0 lg:gap-8"
      />
    </>
  );
};
export const CommentAction: FC<{
  comment: CommentDetailsNested;
  postSlug: string;
  commentsCount: number;
  lang: Lang;
}> = ({ comment, postSlug, commentsCount, ...props }) => {
  const { data: currentUser } = api.user.findSelf.useQuery();
  const editor = useEditorStyled();
  const [openReply, setOpenReply] = useState(false);
  const openReplyEditor = () => {
    if (openReply === false) {
      flushSync(() => setOpenReply(!openReply));
      editor?.commands.focus();
    } else {
      setOpenReply(!openReply);
    }
  };
  return (
    <div>
      <div className="flex w-full justify-between">
        <div className="flex gap-0">
          <ClapButtonForComment
            Icon={<ClapIcon />}
            userId={currentUser?.id}
            commentId={comment.id}
          />
          <Button
            variant="ghost"
            onClick={() => {
              openReplyEditor();
            }}
          >
            <MessageCircleMore />
            {commentsCount === 0 ? "" : commentsCount.toString()}
          </Button>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            openReplyEditor();
          }}
        >
          <ReplyIcon />
          <p>{openReply ? t("Cancel", props.lang) : t("Reply", props.lang)}</p>
        </Button>
      </div>
      {openReply && (
        <div>
          <div className="h-2" />
          <CommentEditor
            postSlug={postSlug}
            parentId={comment.id}
            closeEditor={() => setOpenReply(false)}
            editor={editor}
            lang={props.lang}
          />
        </div>
      )}
    </div>
  );
};
const CommentEditor: FC<{
  parentId?: string;
  postSlug: string;
  closeEditor?: () => void;
  editor: Editor | null;
  lang: Lang;
}> = ({ parentId, postSlug, closeEditor, editor, ...props }) => {
  const { data: currentUser } = api.user.findSelf.useQuery();
  const queryClient = api.useUtils();
  const { mutate, isPending: isLoading } = api.comment.create.useMutation({
    onSuccess: async (data) => {
      // if there is close editor, do so.
      // clear the editor if the editor is always open
      closeEditor?.();
      editor?.commands.clearContent();
      await queryClient.comment.findMany.cancel({
        slug: postSlug,
      });
      const previousQuery = queryClient.comment.findMany.getData({
        slug: postSlug,
      });
      if (previousQuery !== undefined) {
        const newComment = {
          ...data,
          children: [], // there can be no children for a new comment
        };
        if (data.parentId === null) {
          queryClient.comment.findMany.setData(
            {
              slug: postSlug,
            },
            [...previousQuery, newComment],
          );
        } else {
          //find parent comment
          const newCommentTree: CommentDetailsNested[] = previousQuery.map(
            (comment: CommentDetailsNested) => {
              {
                // depth 0
              }
              if (comment.id === data.parentId) {
                return {
                  ...comment,
                  children: [...comment.children, newComment],
                };
              } else {
                return {
                  ...comment,
                  children: comment.children.map((comment) => {
                    {
                      // depth 1
                    }
                    if (comment.id === data.parentId) {
                      return {
                        ...comment,
                        children: [...comment.children, newComment],
                      };
                    } else {
                      return {
                        ...comment,
                        children: comment.children.map((comment) => {
                          {
                            // depth 2
                          }
                          if (comment.id === data.parentId) {
                            return {
                              ...comment,
                              children: [...comment.children, newComment],
                            };
                          } else {
                            return {
                              ...comment,
                              children: comment.children.map((comment) => {
                                {
                                  // depth 3
                                }
                                if (comment.id === data.parentId) {
                                  return {
                                    ...comment,
                                    children: [...comment.children, newComment],
                                  };
                                } else {
                                  return {
                                    ...comment,
                                    children: comment.children.map(
                                      (comment) => {
                                        {
                                          // depth 4
                                        }
                                        if (comment.id === data.parentId) {
                                          return {
                                            ...comment,
                                            children: [newComment],
                                          };
                                        } else {
                                          return comment;
                                        }
                                      },
                                    ),
                                  };
                                }
                              }),
                            };
                          }
                        }),
                      };
                    }
                  }),
                };
              }
            },
          );
          queryClient.comment.findMany.setData(
            {
              slug: postSlug,
            },
            newCommentTree,
          );
        }
        setTimeout(() => {
          const element = document.getElementById(data.id);
          element?.scrollIntoView({ behavior: "smooth" });
        }, 10); // need to wait for the element to be placed.
      }
      return { previousQuery };
    },
    onSettled: () => {
      void queryClient.comment.findMany.invalidate({
        slug: postSlug,
      });
    },
  });
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (el) {
      el.addEventListener("click", () => {
        editor?.commands.focus();
      });
    }
    return () => {
      if (el) {
        el.removeEventListener("click", () => {
          editor?.commands.focus();
        });
      }
    };
  }, [ref, editor]);
  const pathname = usePathname();
  if (currentUser === undefined) {
    return (
      <Card>
        <div className="flex h-full min-h-32 w-full items-center justify-center">
          <LoadingSpinner />
        </div>
      </Card>
    );
  }
  if (currentUser === null) {
    return (
      <Link href={`/login?callbackUrl=${encodeURIComponent(pathname)}`}>
        <Card>
          <div className="h-full min-h-32 w-full" />
        </Card>
      </Link>
    );
  }
  if (editor === null) {
    return (
      <Card>
        <div className="flex h-full min-h-32 w-full items-center justify-center">
          <Spinner />
        </div>
      </Card>
    );
  }
  return (
    <Card ref={ref}>
      <CommentAuthorSection author={currentUser} />
      <div className="h-4" />
      <EditorContent
        id={commentEditorId}
        editor={editor}
        className="scroll-mt-[calc(var(--header-height)+var(--main-padding))]"
      />
      <div className="flex justify-end">
        <Button
          variant="secondary"
          onClick={(e) => {
            e.preventDefault();

            const content = editor.getText();
            if (content.length > 0) {
              mutate({
                parentCommentId: parentId,
                postSlug,
                content: editor.getJSON(),
              });
            } else {
              toast.error("The comment is empty");
            }
          }}
        >
          {isLoading ? (
            <LoadingSpinner backgroundText={t("Submit", props.lang, true)} />
          ) : (
            t("Submit", props.lang)
          )}
        </Button>
      </div>
    </Card>
  );
};
const ClapButtonForPost: FC<{
  postSlug: string;
  userId?: string;
  claps: {
    userId: string;
  }[];
  Icon: React.ReactNode;
}> = ({ postSlug, userId, claps, Icon }) => {
  const queryClient = api.useUtils();
  const { mutate: createClapOnPost } = api.clap.createClapOnPost.useMutation({
    onMutate: async ({ postSlug }) => {
      await queryClient.clap.findClapsOnPost.cancel({
        postSlug,
      });
      const previousQuery = queryClient.clap.findClapsOnPost.getData({
        postSlug,
      });
      if (previousQuery != undefined && userId !== undefined) {
        const newClapOnPost = {
          userId: userId,
        };
        queryClient.clap.findClapsOnPost.setData({ postSlug }, [
          ...previousQuery,
          newClapOnPost,
        ]);
      }
      return { previousQuery };
    },
    onSuccess: () => {
      void queryClient.clap.findClapsOnPost.invalidate({ postSlug });
    },
    onError: (error) => {
      console.error(error.message);
      void queryClient.clap.findClapsOnPost.invalidate({ postSlug });
    },
  });
  const { mutate: deleteClapOnPost } = api.clap.deleteClapOnPost.useMutation({
    onMutate: async ({ postSlug }) => {
      await queryClient.clap.findClapsOnPost.cancel({
        postSlug,
      });
      const previousQuery = queryClient.clap.findClapsOnPost.getData({
        postSlug,
      });
      if (previousQuery != undefined && userId !== undefined) {
        queryClient.clap.findClapsOnPost.setData(
          { postSlug },
          previousQuery.filter((clap) => clap.userId !== userId),
        );
      }
      return { previousQuery };
    },
    onSuccess: () => {
      void queryClient.clap.findClapsOnPost.invalidate({ postSlug });
    },
    onError: (error) => {
      console.error(error.message);
      void queryClient.clap.findClapsOnPost.invalidate({ postSlug });
    },
  });
  const { data: currentUser } = api.user.findSelf.useQuery();
  const router = useRouter();
  const pathname = usePathname();
  const isPostClappedByCurrentUser = claps.some(
    (clap) => clap.userId === userId,
  );
  const handleClapOnPost = () => {
    if (currentUser === null) {
      // user is not logged in
      router.push(`/login?callbackUrl=${encodeURIComponent(pathname)}`); // TODO: maintain query params
    }
    if (!isPostClappedByCurrentUser) {
      createClapOnPost({ postSlug });
    } else {
      deleteClapOnPost({ postSlug });
    }
  };
  const clapsCount = claps.length === 0 ? 10 : 10 + claps.length;
  return (
    <Button
      variant="secondary"
      onClick={(e) => {
        e.preventDefault();
        handleClapOnPost();
      }}
    >
      {Icon}
      {clapsCount.toString()}
    </Button>
  );
};
const ClapButtonForComment: FC<{
  userId?: string;
  Icon: React.ReactNode;
  commentId: string;
}> = ({ userId, Icon, commentId }) => {
  const searchParams = useSearchParams();
  const { data } = api.clap.findClapsOnComment.useQuery({ commentId });
  const queryClient = api.useUtils();
  const { mutate: createClapOnComment } =
    api.clap.createClapOnComment.useMutation({
      onMutate: async ({ commentId }) => {
        await queryClient.clap.findClapsOnComment.cancel({
          commentId,
        });
        const previousQuery = queryClient.clap.findClapsOnComment.getData({
          commentId,
        });
        if (previousQuery != undefined && userId !== undefined) {
          const newClapOnPost = {
            userId: userId,
          };
          queryClient.clap.findClapsOnComment.setData({ commentId }, [
            ...previousQuery,
            newClapOnPost,
          ]);
        }
        return { previousQuery };
      },
      onSuccess: () => {
        void queryClient.clap.findClapsOnComment.invalidate({ commentId });
      },
      onError: (error) => {
        console.error(error.message);
        void queryClient.clap.findClapsOnComment.invalidate({ commentId });
      },
    });
  const { mutate: deleteClapOnComment } =
    api.clap.deleteClapOnComment.useMutation({
      onMutate: async ({ commentId }) => {
        await queryClient.clap.findClapsOnComment.cancel({
          commentId,
        });
        const previousQuery = queryClient.clap.findClapsOnComment.getData({
          commentId,
        });
        if (previousQuery != undefined && userId !== undefined) {
          queryClient.clap.findClapsOnComment.setData(
            { commentId },
            previousQuery.filter((clap) => clap.userId !== userId),
          );
        }
        return { previousQuery };
      },
      onSuccess: () => {
        void queryClient.clap.findClapsOnComment.invalidate({ commentId });
      },
      onError: (error) => {
        console.error(error.message);
        void queryClient.clap.findClapsOnComment.invalidate({ commentId });
      },
    });
  const { data: currentUser } = api.user.findSelf.useQuery();
  const router = useRouter();
  const pathname = usePathname();

  if (!data) return null;

  const isClappedByCurrentUser = data.some((clap) => clap.userId === userId);
  const handleClapOnComment = () => {
    if (currentUser === null) {
      // user is not logged in
      const params = new URLSearchParams(searchParams);
      params.set("callbackUrl", pathname);
      router.push(`/login?${params.toString()}`);
    }
    if (!isClappedByCurrentUser) {
      createClapOnComment({ commentId });
    } else {
      deleteClapOnComment({ commentId });
    }
  };
  return (
    <Button
      variant="ghost"
      onClick={(e) => {
        e.preventDefault();
        handleClapOnComment();
      }}
    >
      {Icon}
      {data.length === 0 ? "" : data.length.toString()}
    </Button>
  );
};
